/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../../../Audio/AudioManager';
import { SymbolAnimationOption } from '../../../Symbol/SymbolAnimation';
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import Decorators from '../../Decorators';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import IAnimationEventData from './IAnimationEventData';

@cc._decorator.ccclass
export default class AnimationManager extends ComponentBase
{
    private _animationEventDatas: IAnimationEventData[];
    public get AnimationEventDatas(): IAnimationEventData[] { return this._animationEventDatas; }

    private _symbolId: string = "";
    public get SymbolId(): string { return this._symbolId; }

    private _animationSkeleton: sp.Skeleton = null;

    private _isCurrent: boolean = false;

    private _isPlaying: boolean = false;

    private _audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent<AudioManager>();

    private _maxPaylineDuration: number = 0;
    public get MaxPaylineDuration(): number { return this._maxPaylineDuration; }

    protected setContexts(): void
    {
        super.setContexts();
        this._audioManagerRef.SetContext(AudioManager);
    }

    protected start(): void
    {
        super.start();
        this.node.active = false;
    }

    public Initialize(animationDatas: IAnimationEventData[], symbolId: string): void
    {
        this._symbolId = symbolId;
        this._animationEventDatas = animationDatas;

        // Make copy for each animation manager.
        let animationNode: cc.Node = cc.instantiate(animationDatas[0].AnimationNode);
        this.node.addChild(animationNode);

        this._animationSkeleton = animationNode.getComponent(sp.Skeleton);

        for (const data of animationDatas)
        {
            const anim: cc.AnimationClip = this._animationSkeleton.findAnimation(data.AnimationName);
            if (!anim)
            {
                CLog.warn(`No symbol animation found with name: [${data.AnimationName}].`,
                    LogTypes.Configuration, this);
                continue;
            }

            this.node.parent.on(data.ActiveEventName,
                () => this.setAnimation(data.AnimationName, data.Loop, data.AudioCue));

            if (data.ClearEventName)
            {
                this.node.parent.on(data.ClearEventName, this.onClearHandler);
            }

            if (data.AnimationOption === SymbolAnimationOption.Payline)
            {
                this._maxPaylineDuration = Math.max(this._maxPaylineDuration, anim.duration);
            }
        }

        this._animationSkeleton.setCompleteListener(this.onAnimationCompleteHandler);
    }

    public SetActive(symbolId: string): boolean
    {
        this._isCurrent = symbolId === this._symbolId;
        this.node.active = this._isCurrent;
        return this._isCurrent;
    }

    private setAnimation = (animationName: string, loop: boolean = false, audioCue: string = null): void =>
    {
        if (!this._animationSkeleton) { return; }
        if (this._isPlaying || !this._isCurrent) { return; }

        if (audioCue && audioCue !== '')
        {
            this._audioManagerRef.Value.TriggerSound(null, audioCue);
        }

        if (animationName && animationName !== '')
        {
            this._isPlaying = true;
            this._animationSkeleton.setAnimation(0, animationName, loop);
        }
    }

    @Decorators.BindThis()
    private onClearHandler = (): void =>
    {
        this._animationSkeleton.clearTrack(0);
        this._animationSkeleton.setToSetupPose();
        this._animationSkeleton.updateWorldTransform();

        this._isPlaying = false;
    }

    private onAnimationCompleteHandler = (): void =>
    {
        this._isPlaying = false;
    }
}
